import * as React from 'react';
import { cn } from '@heroui/react';
import type { QuestionnaireSchema } from '../types';
import { QuestionView } from './QuestionView';
import { ResultView } from './ResultView';
import { StartScreen } from './StartScreen';
import type { QuizRendererProps, QuizRendererResult } from './types';
import { useQuizGame } from './useQuizGame';
import { useQuizSchema } from './useQuizSchema';

export function QuizRenderer({
  schema,
  title = '',
  mode = 'prod',
  onFinish,
  onStart,
}: QuizRendererProps) {
  const [startedOnce, setStartedOnce] = React.useState(false);
  const finishSentRef = React.useRef(false);
  const quizQuestions = useQuizSchema(schema as QuestionnaireSchema | null | undefined, { defaultScore: 1 });
  const game = useQuizGame(quizQuestions, { enableSound: false });

  React.useEffect(() => {
    if (game.gameState !== 'finished') {
      finishSentRef.current = false;
      return;
    }
    if (finishSentRef.current) return;
    finishSentRef.current = true;
    const result: QuizRendererResult = {
      score: game.score,
      totalScore: game.maxScore,
      questionCount: game.totalQuestions,
      answers: game.answers,
    };
    onFinish?.(result);
  }, [game.gameState, game.score, game.maxScore, game.totalQuestions, game.answers, onFinish]);

  const quizTitle = title || '互动问答';
  const lastCorrect = game.answers[game.answers.length - 1]?.isCorrect ?? false;

  const handleStart = () => {
    game.startGame();
    if (!startedOnce) {
      setStartedOnce(true);
      onStart?.();
    }
  };

  return (
    <div
      className={cn(
        'relative flex min-h-[420px] w-full flex-col overflow-hidden rounded-xl text-white transition-colors duration-1000',
        'bg-gradient-to-b from-slate-950 via-indigo-950 to-slate-950'
      )}
    >
      <div className="pointer-events-none absolute -left-[10%] top-[-20%] h-[500px] w-[500px] animate-pulse rounded-full bg-indigo-600/30 blur-[100px]" />
      <div className="pointer-events-none absolute -right-[10%] bottom-[-20%] h-[400px] w-[400px] animate-pulse rounded-full bg-purple-600/25 blur-[100px]" />

      <main className="relative z-10 flex min-h-[420px] flex-1 flex-col">
        {game.gameState === 'start' ? (
          <StartScreen title={quizTitle} onStart={handleStart} />
        ) : game.gameState === 'finished' ? (
          <ResultView
            score={game.score}
            totalScore={game.maxScore}
            questionCount={game.totalQuestions}
            mode={mode}
            onRestart={() => {
              game.restart();
            }}
          />
        ) : (
          <QuestionView
            question={game.currentQuestion}
            quizTitle={quizTitle}
            gameState={game.gameState}
            selectedIds={game.selectedIds}
            currentIndex={game.currentIndex}
            total={game.totalQuestions}
            progressPercent={game.progressPercent}
            lastAnswerCorrect={lastCorrect}
            onSelect={game.handleOptionToggle}
            onSubmitMultiple={game.submitCurrentMultiple}
            onNext={game.goNext}
          />
        )}
      </main>
    </div>
  );
}
